import { Controller } from '@hotwired/stimulus'

// Reloads a lab-test product's vendor-specific section (price + test info +
// add-to-cart) when the member switches lab company. Selecting a radio points
// the Turbo Frame's src at the variant endpoint for the chosen company; Turbo
// fetches and swaps the frame in place, and react_ujs re-mounts the price block
// on turbo:frame-load. Built fresh for this reload-on-change pattern rather than
// copied from the membership toggle, which is plain CSS with no frame reload.
//
// Avoiding a layout shift while switching: the swapped-in fallback markup is
// briefly shorter than the React-mounted price block, so without intervention
// the frame collapses then grows -- everything below it jumps up then back down.
// We reserve the frame's pre-switch height as a min-height the moment a switch
// starts and release it once the new content (including the re-mounted React
// block) has settled, so nothing below the frame moves during the swap.
//
// The picker shows the lab-company name only (access/premium switch style); the
// per-vendor price and one-time service fee live in the price block below, which
// the React block keeps cart-aware. The selected card's border-checked highlight
// is resolved server-side and re-renders with each Turbo-Frame swap.
//
// Address-bar accuracy: on initial load a preference (or org default) can resolve
// to a vendor other than the navigated permalink, and a switch changes the shown
// vendor without navigating; in both cases we replaceState to the displayed
// variant's product path so a shared/bookmarked link reopens what's on screen.
// Replace, not push -- a toggle click is not a new history entry; selection
// precedence is unchanged, the URL only mirrors the resolved selection.
export default class extends Controller {
  static values = { url: String, frame: String, path: String }

  connect() {
    this.onFrameLoad = this.onFrameLoad.bind(this)
    document.addEventListener('turbo:frame-load', this.onFrameLoad)
    // Initial load: a preference / org default can resolve to a vendor other
    // than the navigated permalink, so point the URL at what's actually shown.
    this.syncAddressBar()
  }

  disconnect() {
    document.removeEventListener('turbo:frame-load', this.onFrameLoad)
  }

  select(event) {
    const { frame } = this
    if (!frame) return

    frame.style.minHeight = `${frame.offsetHeight}px`

    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set('lab_company', event.target.value)
    frame.setAttribute('src', `${url.pathname}${url.search}`)
  }

  onFrameLoad(event) {
    if (event.target !== this.frame) return

    // Swapped-in content carries the newly selected variant, so mirror it into
    // the URL, then drop the height floor select() reserved for the swap.
    this.syncAddressBar()
    this.releaseReservedHeight()
  }

  releaseReservedHeight() {
    const { frame } = this
    if (!frame || !frame.style.minHeight) return

    // Wait for the React price block to re-mount and paint (its lazy chunk is
    // already loaded from the initial render) before dropping the reserved
    // floor, so the frame fits the new variant without re-collapsing.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      frame.style.minHeight = ''
    }))
  }

  syncAddressBar() {
    if (!this.hasPathValue) return
    if (window.location.pathname === this.pathValue) return

    const url = `${this.pathValue}${window.location.search}${window.location.hash}`
    window.history.replaceState(window.history.state, '', url)
  }

  get frame() {
    return document.getElementById(this.frameValue)
  }
}
